import React, { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ChevronLeft, 
  TrendingUp, 
  DollarSign, 
  AlertCircle,
  Activity,
  CheckCircle,
  XCircle,
  Clock,
  Percent
} from 'lucide-react';
import { useGetExpenditureActivityTreeQuery } from '../services/api';
import img1 from '../../../assets/pmshribanner.webp';
import { UtilizationGauge, TrendLineChart } from '../components/charts';
import type { ExpenditureActivityTreeNode } from '../types';
import Header from '../../../components/header/Header';
import HeroCarousel from '../../../components/carousel/HeroCarousel';
import KVSLoader from '../../../components/loaders/KVSLoader';
import Breadcrumb from '../../../shared/components/Breadcrumb';
import Footer from '../../../components/footer/Footer';

interface SubActivity {
  id: number;
  code: string;
  name: string;
  description: string;
  activityId: number;
  activityName: string;
  activityCode: string;
  subheadId: number;
  subheadName: string;
  headId: number;
  headName: string;
  expenditureType: 'recurring' | 'non-recurring';
  sanctionedAmount: number;
  spentAmount: number;
  unspentAmount: number;
  utilizationPercentage: number;
  status: 'fully_utilized' | 'partially_utilized' | 'not_utilized' | 'pending';
}

const SubActivityAnalyticsScreen: React.FC = () => {
  const navigate = useNavigate();
  const { headCode, subheadId, activityId, subActivityId } = useParams<{ 
    headCode: string; 
    subheadId: string; 
    activityId: string;
    subActivityId: string;
  }>();
  const carouselImages = [img1];

  // Get data from Expenditure API
  const { data: expenditureTreeData, isLoading } = useGetExpenditureActivityTreeQuery({ });

  // Find the hierarchy: head -> subhead -> activity -> subactivity
  const { head, subhead, activity, subActivity } = useMemo(() => {
    if (!expenditureTreeData?.success || !expenditureTreeData?.data?.tree || !headCode || !subheadId || !activityId || !subActivityId) {
      return { head: null, subhead: null, activity: null, subActivity: null };
    }

    const headNumber = parseInt(String(headCode).replace('H', ''));
    const headFromAPI = expenditureTreeData.data.tree.find(
      (h: ExpenditureActivityTreeNode, idx: number) => h.id === headNumber || idx === headNumber - 1
    );

    if (!headFromAPI) return { head: null, subhead: null, activity: null, subActivity: null };

    const subheadFromAPI = headFromAPI.children?.find(
      (sh: ExpenditureActivityTreeNode) => sh.id === parseInt(subheadId)
    );

    if (!subheadFromAPI) return { head: headFromAPI, subhead: null, activity: null, subActivity: null };

    const activityFromAPI = subheadFromAPI.children?.find(
      (act: ExpenditureActivityTreeNode) => act.id === parseInt(activityId)
    );

    if (!activityFromAPI) return { head: headFromAPI, subhead: subheadFromAPI, activity: null, subActivity: null };

    const subActivityFromAPI = activityFromAPI.children?.find(
      (subAct: ExpenditureActivityTreeNode) => subAct.id === parseInt(subActivityId)
    );

    if (!subActivityFromAPI) return { head: headFromAPI, subhead: subheadFromAPI, activity: activityFromAPI, subActivity: null };

    // Transform the data
    const sanctionedAmount = subActivityFromAPI.sanTotal || 0;
    const spentAmount = subActivityFromAPI.spentTotal || 0;
    const utilization = sanctionedAmount > 0 ? (spentAmount / sanctionedAmount) * 100 : 0;
    const status: 'fully_utilized' | 'partially_utilized' | 'not_utilized' | 'pending' = 
      utilization >= 90 ? 'fully_utilized' :
      utilization >= 50 ? 'partially_utilized' :
      utilization > 0 ? 'not_utilized' : 'pending';

    // Determine expenditure type
    const expenditureType: 'recurring' | 'non-recurring' = 
      (activityFromAPI.sanRec || 0) >= (activityFromAPI.sanNonRec || 0) ? 'recurring' : 'non-recurring';

    const transformedSubActivity: SubActivity = {
      id: subActivityFromAPI.id,
      code: `${headFromAPI.id}.${subheadFromAPI.id}.${activityFromAPI.id}.${subActivityFromAPI.id}`,
      name: subActivityFromAPI.title,
      description: `Sub-activity under ${activityFromAPI.title}`,
      activityId: activityFromAPI.id,
      activityName: activityFromAPI.title,
      activityCode: `${headFromAPI.id}.${subheadFromAPI.id}.${activityFromAPI.id}`,
      subheadId: subheadFromAPI.id,
      subheadName: subheadFromAPI.title,
      headId: headFromAPI.id,
      headName: headFromAPI.title,
      expenditureType,
      sanctionedAmount,
      spentAmount,
      unspentAmount: sanctionedAmount - spentAmount,
      utilizationPercentage: utilization,
      status,
    };

    return { 
      head: headFromAPI, 
      subhead: subheadFromAPI, 
      activity: activityFromAPI, 
      subActivity: transformedSubActivity 
    };
  }, [expenditureTreeData, headCode, subheadId, activityId, subActivityId]);

  // Format currency
  const formatCurrency = (value: number): string => {
    return `₹${value.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
  };

  // Get status badge
  const getStatusBadge = (status: string) => {
    const badges = {
      fully_utilized: (
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-green-100 text-green-700 border border-green-300">
          <CheckCircle className="w-3.5 h-3.5" />
          Fully Utilized
        </span>
      ),
      partially_utilized: (
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-yellow-100 text-yellow-700 border border-yellow-300">
          <Activity className="w-3.5 h-3.5" />
          Partially Utilized
        </span>
      ),
      not_utilized: (
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-red-100 text-red-700 border border-red-300">
          <XCircle className="w-3.5 h-3.5" />
          Under-Utilized
        </span>
      ),
      pending: (
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-gray-100 text-gray-700 border border-gray-300">
          <Clock className="w-3.5 h-3.5" />
          Pending
        </span>
      ),
    };
    return badges[status as keyof typeof badges] || badges.pending;
  };

  // Generate mock trend data
  const trendData = useMemo(() => {
    if (!subActivity) return [];
    const months = ['Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar'];
    const currentYear = new Date().getFullYear();
    const sanctioned = subActivity.sanctionedAmount;
    const spent = subActivity.spentAmount;
    return months.map((month, idx) => {
      const monthSpent = (spent / 12) * (idx + 1) * (0.8 + Math.random() * 0.4);
      return {
        month,
        year: currentYear,
        sanctioned: sanctioned / 12,
        received: sanctioned / 12,
        spent: monthSpent,
        utilization: (monthSpent / (sanctioned / 12)) * 100,
        recurringSpent: subActivity.expenditureType === 'recurring' ? monthSpent : 0,
        nonRecurringSpent: subActivity.expenditureType === 'non-recurring' ? monthSpent : 0,
      };
    });
  }, [subActivity]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-[#f5f5f7]">
        <Header />
        <div className="flex-grow flex flex-col items-center justify-center">
          <KVSLoader size={80} />
          <span className="mt-4 text-gray-600">Loading...</span>
        </div>
        <Footer />
      </div>
    );
  }

  if (!subActivity) {
    return (
      <div className="min-h-screen flex flex-col bg-[#f5f5f7]">
        <Header />
        <HeroCarousel images={carouselImages} autoRotateInterval={3000} />
        <div className="w-full max-w-7xl mx-auto px-4 pt-2 sm:pt-3">
          <Breadcrumb />
        </div>
        <main className="flex-grow w-full max-w-7xl mx-auto px-4 py-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-red-900">Sub-Activity Not Found</h3>
              <p className="text-sm text-red-700 mt-1">
                The requested sub-activity could not be found. Please check the URL and try again.
              </p>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#f5f5f7]">
      <Header />
      <HeroCarousel images={carouselImages} autoRotateInterval={3000} />

      <div className="w-full max-w-7xl mx-auto px-4 pt-2 sm:pt-3">
        <Breadcrumb />
      </div>

      <main className="flex-grow w-full max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(`/pm-shri/analytics/heads/${headCode}/subheads/${subheadId}/activities/${activityId}`)}
                className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-5 h-5 text-white" />
              </button>
              <div>
                <div className="text-white/80 text-sm font-medium mb-1">
                  {head?.title} → {subhead?.title} → {activity?.title}
                </div>
                <h1 className="text-2xl font-bold text-white">Sub-Activity Details</h1>
              </div>
            </div>
          </div>
        </div>

        {/* Sub-Activity Info Card */}
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-6 border-2 border-purple-100">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">{subActivity.name}</h2>
                    <p className="text-sm text-gray-600 mb-3">{subActivity.description}</p>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="px-3 py-1.5 rounded-lg text-xs font-bold bg-white text-purple-700 border border-purple-200">
                        📋 {subActivity.code}
                      </span>
                      <span className="px-3 py-1.5 rounded-lg text-xs font-bold bg-white text-blue-700 border border-blue-200">
                        {subActivity.expenditureType === 'recurring' ? '🔄 Recurring' : '💰 Non-Recurring'}
                      </span>
                      {getStatusBadge(subActivity.status)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-center">
              <UtilizationGauge value={subActivity.utilizationPercentage} size="lg" label="Utilization" />
            </div>
          </div>
        </div>
      </div>

      {/* Financial Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Sanctioned Amount */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden border-t-4 border-blue-500">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-blue-100 rounded-xl">
                <DollarSign className="w-6 h-6 text-blue-600" />
              </div>
              <div className="text-right">
                <div className="text-sm font-medium text-gray-600 mb-1">Total Sanctioned</div>
                <div className="text-2xl font-bold text-gray-900">{formatCurrency(subActivity.sanctionedAmount)}</div>
                <div className="text-xs text-gray-500 mt-1">100% of budget</div>
              </div>
            </div>
          </div>
        </div>

        {/* Spent Amount */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden border-t-4 border-green-500">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-green-100 rounded-xl">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
              <div className="text-right">
                <div className="text-sm font-medium text-gray-600 mb-1">Total Spent</div>
                <div className="text-2xl font-bold text-green-600">{formatCurrency(subActivity.spentAmount)}</div>
                <div className="text-xs text-gray-500 mt-1">
                  {subActivity.utilizationPercentage.toFixed(1)}% utilized
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Unspent Amount */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden border-t-4 border-orange-500">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-orange-100 rounded-xl">
                <AlertCircle className="w-6 h-6 text-orange-600" />
              </div>
              <div className="text-right">
                <div className="text-sm font-medium text-gray-600 mb-1">Remaining Budget</div>
                <div className="text-2xl font-bold text-orange-600">{formatCurrency(subActivity.unspentAmount)}</div>
                <div className="text-xs text-gray-500 mt-1">
                  {(100 - subActivity.utilizationPercentage).toFixed(1)}% available
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Expenditure Trend */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-purple-600" />
            Monthly Expenditure Trend
          </h3>
          <TrendLineChart data={trendData} dataKeys={["spent", "utilization"]} colors={{ spent: '#8b5cf6', utilization: '#f59e0b' }} showLegend />
        </div>

        {/* Utilization Breakdown */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Percent className="w-5 h-5 text-blue-600" />
            Utilization Breakdown
          </h3>
          <div className="space-y-4">
            {/* Progress Bar */}
            <div>
              <div className="flex justify-between text-sm font-medium text-gray-700 mb-2">
                <span>Budget Utilization</span>
                <span>{subActivity.utilizationPercentage.toFixed(2)}%</span>
              </div>
              <div className="w-full h-6 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${
                    subActivity.utilizationPercentage >= 90 ? 'bg-green-500' :
                    subActivity.utilizationPercentage >= 75 ? 'bg-green-400' :
                    subActivity.utilizationPercentage >= 50 ? 'bg-yellow-500' : 
                    subActivity.utilizationPercentage > 0 ? 'bg-red-500' : 'bg-gray-300'
                  }`}
                  style={{ width: `${Math.min(subActivity.utilizationPercentage, 100)}%` }}
                />
              </div>
            </div>

            {/* Financial Details */}
            <div className="grid grid-cols-2 gap-4 pt-4">
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="text-xs font-medium text-blue-700 mb-1">Sanctioned</div>
                <div className="text-lg font-bold text-blue-900">{formatCurrency(subActivity.sanctionedAmount)}</div>
              </div>
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="text-xs font-medium text-green-700 mb-1">Spent</div>
                <div className="text-lg font-bold text-green-900">{formatCurrency(subActivity.spentAmount)}</div>
              </div>
              <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                <div className="text-xs font-medium text-orange-700 mb-1">Remaining</div>
                <div className="text-lg font-bold text-orange-900">{formatCurrency(subActivity.unspentAmount)}</div>
              </div>
              <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                <div className="text-xs font-medium text-purple-700 mb-1">Type</div>
                <div className="text-sm font-bold text-purple-900 capitalize">
                  {subActivity.expenditureType.replace('_', ' ')}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Additional Information */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Hierarchy Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="text-xs font-medium text-gray-600 mb-2">Head</div>
            <div className="font-semibold text-gray-900">{subActivity.headName}</div>
            <div className="text-xs text-gray-500 mt-1 font-mono">ID: {subActivity.headId}</div>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="text-xs font-medium text-gray-600 mb-2">Subhead</div>
            <div className="font-semibold text-gray-900">{subActivity.subheadName}</div>
            <div className="text-xs text-gray-500 mt-1 font-mono">ID: {subActivity.subheadId}</div>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="text-xs font-medium text-gray-600 mb-2">Activity</div>
            <div className="font-semibold text-gray-900">{subActivity.activityName}</div>
            <div className="text-xs text-gray-500 mt-1 font-mono">{subActivity.activityCode}</div>
          </div>
          <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
            <div className="text-xs font-medium text-purple-700 mb-2">Sub-Activity</div>
            <div className="font-semibold text-purple-900">{subActivity.name}</div>
            <div className="text-xs text-purple-600 mt-1 font-mono">{subActivity.code}</div>
          </div>
        </div>
      </div>
      </main>

      <Footer />
    </div>
  );
};

export default SubActivityAnalyticsScreen;
